import {Product} from "../../../../model/Product";
import {PossibleMatch} from "./PossibleMatch";

/**
 * Fuzzy match between an incoming order line and a Product in the same
 * organization.
 *
 * The incoming side may be identified by more than one value (an ecommerce
 * item carries both a `sku` and a `title`, a CSV row carries a single product
 * code), and the Product side is identified by either its `title` or the
 * customer's own `customerProduct_ID`. Every one of those values is tokenized
 * the same way and the candidates are ranked by how many tokens they share.
 */
/**
 * The Product field an incoming order-line code is expected to equal.
 *
 * Mirrors `EcommerceConnection.productIdentifierType` -- see
 * `docs/contracts/shared/EcommerceConnection.md`. `title` is not one of the
 * server's modes; it exists for callers (the CSV import) that have nothing
 * better to go on.
 */
export type ProductMatchField = 'customerProduct_ID' | 'EAN13' | 'ID' | 'title';

/**
 * Tells the matcher that the incoming code is known to be a particular Product
 * field, rather than just some free text to be scored against everything.
 *
 * When the connection nominates a field, an equal value there is an assignment
 * the customer made, not a coincidence of shared words.
 */
export interface ProductMatchPreference {
  /** The Product field the incoming code is expected to equal. */
  field: ProductMatchField;
  /** The incoming value (the order line's sku, or its remoteProductId). */
  term: string;
}

/** How closely a candidate satisfied the preference. */
export type PreferredMatchKind = 'EXACT' | 'NEAR' | 'NONE';

export class ProductCode {
  // Tokens that carry no identifying value and would otherwise give every
  // product in the catalog a non-zero score.
  private static readonly STOP_WORDS = ['a', 'an', 'and', 'the', 'of', 'for', 'to', 'in', 'or', 'de', 'la', 'el'];

  // How much more a shared token in the preferred field counts for than the
  // same token found in any other field. Only affects ordering, never score.
  private static readonly PREFERRED_FIELD_WEIGHT = 2;

  /** All incoming values joined, kept for display / debugging. */
  csvProductCode: string;
  /** The individual incoming values (e.g. [sku, title]) that are being matched. */
  searchTerms: string[] = [];
  /** Deduped tokens taken from every search term. */
  searchParts: string[] = [];

  /** Optional hint about which Product field the incoming code should equal. */
  preference: ProductMatchPreference | null = null;
  /** Tokens of `preference.term` only, used for the preferred-field weighting. */
  preferredParts: string[] = [];
  /** Set when some product matched `preference` byte-for-byte. */
  hasExactMatch = false;
  /** Set when some product matched `preference` only after normalization. */
  hasNearExactMatch = false;

  product: Product;
  product_ID: number;
  possibleMatches: PossibleMatch[] = [];
  minPossibleMatchIntersections = 9999;
  maxPossibleMatchIntersections = 0;

  constructor (csvProductCode: string | Array<string | null | undefined>, products: Product[], preference?: ProductMatchPreference) {
    const terms = (Array.isArray(csvProductCode) ? csvProductCode : [csvProductCode])
      .filter(term => term !== undefined && term !== null && String(term).trim().length > 0)
      .map(term => String(term).trim());
    this.searchTerms = terms.filter((v, i, a) => a.indexOf(v) === i);
    this.csvProductCode = this.searchTerms.join(' ');
    this.preference = ( preference && preference.term && String(preference.term).trim().length > 0 ) ? preference : null;
    this.identifyPossibleMatches(products);
  }

  setProduct( product: Product ) {
    this.product = product;
    if ( this.product ) {
      this.product_ID = this.product.ID;
    } else {
      this.product_ID = 0;
    }
  }

  identifyPossibleMatches(products: Product[]) {
    this.possibleMatches = [];
    this.minPossibleMatchIntersections = 9999;
    this.maxPossibleMatchIntersections = 0;
    this.hasExactMatch = false;
    this.hasNearExactMatch = false;

    // Tokenize each incoming value separately and pool the results, so a sku
    // and a title contribute independently.
    let searchParts: string[] = [];
    for ( const term of this.searchTerms ) {
      searchParts = searchParts.concat(this.getParts(term));
    }
    this.searchParts = searchParts.filter((v, i, a) => a.indexOf(v) === i);
    this.preferredParts = this.preference ? this.getParts(this.preference.term) : [];

    if ( !products || products.length === 0 || this.searchParts.length === 0 ) {
      return;
    }

    for ( const product of products) {
      const titleParts = this.getParts(product.title);
      const customerParts = this.getParts(product.customerProduct_ID);
      // Include the nominated field in the token pool as well, otherwise an
      // EAN13 or internal-id connection produces no candidates at all: those
      // values appear in neither the title nor the customer product id.
      const preferredFieldParts = this.preference ? this.getParts(this.getFieldValue(product, this.preference.field)) : [];
      const parts = this.unique(this.unique(titleParts, customerParts), preferredFieldParts);
      const intersections = this.getIntersections(this.searchParts, parts);
      const kind = this.classifyPreferredMatch(product);

      if ( intersections > 0 || kind !== 'NONE' ) {
        if ( this.maxPossibleMatchIntersections < intersections ) {
          this.maxPossibleMatchIntersections = intersections;
        }
        if ( this.minPossibleMatchIntersections > intersections ) {
          this.minPossibleMatchIntersections = intersections;
        }

        // Weight overlap found in the field the connection says carries the
        // identifier. This only moves candidates up the list -- it never
        // invents a match that plain token overlap did not already find.
        const preferredHits = this.getIntersections(this.preferredParts, preferredFieldParts);
        let rank: number;
        let score: number;
        if ( kind === 'EXACT' ) {
          rank = Number.MAX_SAFE_INTEGER;
          score = 1;
          this.hasExactMatch = true;
        } else if ( kind === 'NEAR' ) {
          // Ranked above every token-overlap candidate but deliberately below
          // 1.0, so it renders as "look at this" rather than "this is right"
          // and does not trip the caller's auto-select-on-1.0 rule. The server
          // compares exactly, so this will NOT match on future orders.
          rank = Math.floor(Number.MAX_SAFE_INTEGER / 2);
          score = 0.95;
          this.hasNearExactMatch = true;
        } else {
          rank = intersections + (preferredHits * ProductCode.PREFERRED_FIELD_WEIGHT);
          // Absolute confidence: what fraction of the incoming tokens were
          // found on this product. Independent of how the others scored.
          score = Math.min(1, intersections / this.searchParts.length);
        }

        const matchedOn = kind !== 'NONE'
          ? this.preference.field
          : (this.getIntersections(this.searchParts, customerParts) > this.getIntersections(this.searchParts, titleParts) ? 'customerProduct_ID' : 'title');

        this.addObjectInOrder(new PossibleMatch(intersections, product, {
          score: score,
          exact: kind === 'EXACT',
          nearExact: kind === 'NEAR',
          matchedOn: matchedOn,
          rank: rank
        }), this.possibleMatches);
      }
    }
    if ( this.possibleMatches.length > 0 ) {
      this.setProduct(this.possibleMatches[0].product);
    }
  }

  /**
   * How closely this product satisfies the preference.
   *
   * `EXACT` is byte-for-byte, case-sensitive equality -- exactly what the
   * server's order ingestion does, so it is the only result we can promise
   * will also resolve automatically on future orders.
   *
   * `NEAR` means the values agree only once case and separators are ignored
   * ("MATH-1A" vs "math 1a"). Almost certainly the same product to a human,
   * but the pipeline will not match it, so it is reported distinctly instead
   * of being quietly rounded up to a certainty.
   */
  private classifyPreferredMatch(product: Product): PreferredMatchKind {
    if ( !this.preference || !product ) {
      return 'NONE';
    }
    const value = this.getFieldValue(product, this.preference.field);
    const term = this.preference.term === undefined || this.preference.term === null ? '' : String(this.preference.term);
    if ( value.length === 0 || term.length === 0 ) {
      return 'NONE';
    }
    if ( value === term ) {
      return 'EXACT';
    }
    const compactValue = this.compact(value);
    if ( compactValue.length > 0 && compactValue === this.compact(term) ) {
      return 'NEAR';
    }
    return 'NONE';
  }

  /** The product's value for one of the nominated fields, as a string. */
  private getFieldValue(product: Product, field: ProductMatchField): string {
    if ( !product ) {
      return '';
    }
    let raw: any;
    switch ( field ) {
      case 'customerProduct_ID': raw = product.customerProduct_ID; break;
      case 'EAN13': raw = product.EAN13; break;
      case 'ID': raw = product.ID; break;
      case 'title': raw = product.title; break;
      default: raw = null;
    }
    if ( raw === undefined || raw === null ) {
      return '';
    }
    return String(raw);
  }

  /** Lower-cased, alphanumerics only. '' for anything blank. */
  private compact(value: string | null | undefined): string {
    if ( value === undefined || value === null ) {
      return '';
    }
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  addObjectInOrder(object: PossibleMatch, array: Array<PossibleMatch>) {
    if ( object && array ) {
      // Walk forward to the first entry this one outranks and insert there, so
      // the array stays sorted best-first and ties keep their arrival order.
      let i = 0;
      for (; i < array.length && array[i].rank >= object.rank; i++) {
      }
      array.splice(i, 0, object);
    }
  }

  getIntersections(listA: string[], listB: string[]): number {
    let intersectionCount = 0;
    for ( const value of listA) {
      if ( listB.includes(value) ) {
        intersectionCount++;
      }
    }
    return intersectionCount;
  }

  /**
   * Break a value into comparable tokens.
   *
   * The previous implementation only produced tokens when the value contained
   * a space, a dash or an underscore, so a single-token sku ("ABC123") or a
   * one word title produced an empty list, scored zero against everything and
   * the match indicator never appeared at all. Matching is also case
   * insensitive now -- "MATH1A" and "Math 1A" describe the same book.
   */
  getParts(data: string): string[] {
    if ( !data ) {
      return [];
    }
    const value = String(data);
    const normalized = value
      .replace(/([a-z])([A-Z])/g, '$1 $2')     // camelCase -> camel Case
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')             // any punctuation is a separator
      .replace(/([a-z])(\d)/g, '$1 $2')        // abc123 -> abc 123
      .replace(/(\d)([a-z])/g, '$1 $2');       // 1a -> 1 a

    const parts = normalized
      .split(' ')
      .filter(part => part.length > 0)
      .filter(part => ProductCode.STOP_WORDS.indexOf(part) === -1);

    // Also keep the whole value with all separators removed, so "ABC-123",
    // "ABC 123" and "ABC123" all share a token.
    const compact = value.toLowerCase().replace(/[^a-z0-9]+/g, '');
    if ( compact.length > 0 ) {
      parts.push(compact);
    }
    return parts.filter((v, i, a) => a.indexOf(v) === i);
  }

  unique(list1: string[], list2: string[]) {
    const list = new Array();
    list1.forEach(value => { list.push(value); });
    list2.forEach(value => { list.push(value); });
    return list.filter((v, i, a) => a.indexOf(v) === i);
  }

  /**
   * The top `maxNumber` candidates.
   *
   * NOTE: this used to be `splice(maxNumber, length)`, which both returned the
   * *worst* candidates (splice returns what it removed) and permanently
   * truncated `possibleMatches`. Since it is called straight from a template it
   * ran on every change detection pass, so the list emptied itself and the
   * match chips disappeared. It must not mutate.
   */
  getBestPossibleMatches(maxNumber: number): PossibleMatch[] {
    if ( !this.possibleMatches ) {
      return [];
    }
    if ( this.possibleMatches.length <= maxNumber) {
      return this.possibleMatches;
    }
    return this.possibleMatches.slice(0, maxNumber);
  }
}
